import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { Blueprint } from '../../../models/blueprint.model';
import { BlueprintsSelectorComponent } from '../blueprints-selector.component';
import {
  NamedBlueprintSuggestion,
  blueprintSuggestionPercent,
  resolveNamedBlueprintSuggestions,
} from '../blueprint-suggestions';
import { CustomTreeProposalSummary } from '../custom-quote-tree';

/**
 * Reusable modal for picking a blueprint from a list.
 *
 * It is deliberately independent of any editor: it only emits the selected
 * blueprint via {@link blueprintConfirmed}. Callers decide what to do with the
 * selection (e.g. expand it into quote items, or add it as a blueprint item).
 * Optional Skip (e.g. drawing flow) emits {@link skipped} instead of a blueprint.
 * Optional AI draft tree emits {@link customTreeConfirmed} without pretending
 * the draft is a Blueprint.
 */
@Component({
  selector: 'app-blueprint-selector-modal',
  imports: [
    Dialog,
    Button,
    BlueprintsSelectorComponent,
    Tooltip,
    MessageModule,
  ],
  templateUrl: './blueprint-selector-modal.component.html',
})
export class BlueprintSelectorModalComponent implements OnChanges {
  @Input() visible = false;
  @Input() blueprints: Blueprint[] = [];
  @Input() header = 'Add Blueprint';
  @Input() confirmLabel = 'Add Blueprint';
  @Input() showSkip = false;
  @Input() skipHint = 'Or skip to add a single item without a blueprint';
  @Input() skipTooltip =
    'Add a single item with this drawing and no blueprint template';
  @Input() notice: string | null = null;
  /** Drawing suggestion names. Empty for ordinary blueprint picking. */
  @Input() nameSuggestions: NamedBlueprintSuggestion[] = [];
  /**
   * When set, show the AI draft tree next to the catalogue list so the
   * user can compare it with a close-match blueprint.
   */
  @Input() customTreeProposal: CustomTreeProposalSummary | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() blueprintConfirmed = new EventEmitter<Blueprint>();
  @Output() customTreeConfirmed = new EventEmitter<void>();
  @Output() skipped = new EventEmitter<void>();

  selectedBlueprint: Blueprint | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    const opened = changes['visible']?.currentValue === true;
    const catalogueChanged =
      !!changes['nameSuggestions'] || !!changes['blueprints'];
    if (!this.visible || this.selectedBlueprint) {
      return;
    }
    if (!opened && !catalogueChanged) {
      return;
    }
    const bestMatch = resolveNamedBlueprintSuggestions(
      this.blueprints,
      this.nameSuggestions,
    )[0];
    if (bestMatch) {
      this.selectedBlueprint = bestMatch.blueprint;
    }
  }

  get aiSuggestionPreselected(): boolean {
    const bestMatch = resolveNamedBlueprintSuggestions(
      this.blueprints,
      this.nameSuggestions,
    )[0];
    return !!bestMatch && this.selectedBlueprint?.id === bestMatch.blueprint.id;
  }

  get showCustomTreeProposal(): boolean {
    return this.customTreeProposal != null;
  }

  suggestionPercent(confidence: number): string {
    return blueprintSuggestionPercent(confidence);
  }

  onBlueprintSelected(blueprint: Blueprint | null): void {
    this.selectedBlueprint = blueprint;
  }

  onConfirm(): void {
    if (!this.selectedBlueprint) {
      return;
    }

    this.blueprintConfirmed.emit(this.selectedBlueprint);
    this.close();
  }

  onConfirmCustomTree(): void {
    if (!this.customTreeProposal) {
      return;
    }
    // Leave visibility to the caller so remaining drawings can keep this
    // dialog open. Closing here races PrimeNG's hide and never reopens.
    this.selectedBlueprint = null;
    this.customTreeConfirmed.emit();
  }

  onSkip(): void {
    this.skipped.emit();
    this.close();
  }

  onCancel(): void {
    this.close();
  }

  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.close();
      return;
    }

    this.visible = visible;
    this.visibleChange.emit(visible);
  }

  private close(): void {
    this.selectedBlueprint = null;
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
